import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationCancel, NavigationError, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { HlmToasterImports } from '@spartan-ng/helm/sonner';
import { filter, map, tap } from 'rxjs';
import { AppService } from '../../services/app.service';
import { AuthService } from '../../services/auth.service';
import { AppHeader } from '../app-header/app-header';

@Component({
  selector: 'app-core',
  imports: [AppHeader, RouterOutlet, HlmProgressImports, HlmToasterImports],
  templateUrl: './core.html',
})
export class CoreComponent implements OnInit {
  private readonly appService = inject(AppService);
  public readonly oktaAuthService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public hideNavbar = false;

  /** Global loading indicator driven by the loading interceptor. */
  protected readonly busy = this.appService.busy;
  /** Toasts follow the colour scheme of the page. */
  protected readonly colorScheme = this.appService.colorScheme;

  /**
   * True while the active route is flagged data: { standalone: true }. Such pages (for example
   * access denied) render without the header and the navigation, which are for signed-in users.
   * It is derived from RoutesRecognized, i.e. before the route is activated, so the shell never
   * flashes on screen first.
   */
  private readonly activeRoute = toSignal(this.router.events.pipe(
    map(event => event instanceof RoutesRecognized
      ? event.state.root
      : event instanceof NavigationCancel || event instanceof NavigationError
        ? this.router.routerState.snapshot.root
        : null),
    filter(root => root !== null),
    map(root => deepestChild(root)),
  ), { initialValue: null });

  protected readonly standalonePage = computed(() => this.activeRoute()?.data['standalone'] === true);

  /**
   * True while the active route is flagged data: { focus: true }. Such a page (for example manual
   * entry of a part) brings its own header with a way out, so the app header is left out while the
   * loading indicator and the toasts stay.
   */
  protected readonly focusPage = computed(() => this.activeRoute()?.data['focus'] === true);


  ngOnInit(): void {
    this.appService.setTheme();
    this.appService.checkForUpdates().pipe(
      tap(sw => console.log(sw.type)),
      // Enable for Push notifications
      // switchMap(() => this.appService.requestNotifications())
    ).subscribe();
    this.oktaAuthService.authorize().subscribe();

    this.route.queryParams.subscribe(params => {
      this.hideNavbar = params['hideNavbar'] === 'true';
    });
  }


}

function deepestChild(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let route = root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route;
}
